"use client";

import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/form-elements";
import { generatePulseReportAction } from "@/actions/decision-actions";
import { useRouter } from "next/navigation";
import {
  Sparkles,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  AlertTriangle,
  History,
  Info,
  Calendar,
} from "lucide-react";

interface LogItem {
  id: string;
  nodeId: string;
  nodeType: string;
  eventType: string;
  description: string;
  createdAt: string;
  authorName: string | null;
}

interface BriefingData {
  overallWorkspaceScore: number;
  scoreDirection: "up" | "down" | "stable";
  date: string;
  recapYesterday: string[];
  todayAlerts: string[];
  aiRecommendation: string;
}

interface DailyPulseClientProps {
  initialLogs: LogItem[];
  initialBriefing: BriefingData | null;
}

export function DailyPulseClient({
  initialLogs,
  initialBriefing,
}: DailyPulseClientProps) {
  const router = useRouter();
  const [logs, setLogs] = useState<LogItem[]>(initialLogs);
  const [briefing, setBriefing] = useState<BriefingData | null>(
    initialBriefing,
  );
  const [customPrompt, setCustomPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleRegenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const res = await generatePulseReportAction(customPrompt);
      if (res.error) {
        setMessage(`Errore: ${res.error}`);
      } else if (res.briefing) {
        setBriefing(res.briefing as any);
        setMessage("Briefing rigenerato con successo dall'AI!");
        router.refresh();
      }
    } catch (err: any) {
      setMessage(`Errore di sistema: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getNodeBadgeVariant = (type: string) => {
    switch (type.toLowerCase()) {
      case "strategy":
        return "strategy";
      case "work":
        return "work";
      case "people":
        return "people";
      case "finance":
        return "finance";
      case "risk":
        return "risk";
      default:
        return "neutral";
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* AI Briefing Panel (Left / Main) */}
      <div className="lg:col-span-2 space-y-6">
        {briefing ? (
          <Card className="relative overflow-hidden border-t-4 border-t-primary-500">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary-500 animate-pulse" />
                  <CardTitle className="text-lg font-bold text-text-primary">
                    AI Daily Pulse Briefing
                  </CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-text-muted" />
                  <span className="text-xs text-text-secondary font-semibold">
                    {briefing.date}
                  </span>
                </div>
              </div>
              <CardDescription>
                Rapporto strategico personalizzato basato sulle ultime attività
                registrate sul grafo aziendale.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Score Indicator */}
              <div className="bg-card-muted/30 border border-border p-4 rounded-xl flex items-center justify-between">
                <div className="space-y-1">
                  <span className="text-xs text-text-secondary font-semibold uppercase block">
                    Workspace Health Score
                  </span>
                  <p className="text-[11px] text-text-muted leading-relaxed">
                    Indice sintetico di stabilità operativa calcolato dall'AI
                    correlando scadenze, budget e carichi di lavoro.
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <span className="text-3xl font-extrabold text-text-primary">
                      {briefing.overallWorkspaceScore}
                    </span>
                    <span className="text-xs text-text-muted">/100</span>
                  </div>
                  <div className="p-2 rounded-lg bg-success-soft text-success border border-success/20">
                    {briefing.scoreDirection === "up" ? (
                      <TrendingUp className="h-5 w-5" />
                    ) : briefing.scoreDirection === "down" ? (
                      <TrendingDown className="h-5 w-5" />
                    ) : (
                      <span className="text-xs font-bold">STABLE</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Recap of Yesterday */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold text-text-primary uppercase tracking-wider flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  Cosa è Successo Ieri
                </h3>
                <ul className="space-y-2">
                  {briefing.recapYesterday.map((item, idx) => (
                    <li
                      key={idx}
                      className="text-xs text-text-secondary leading-relaxed pl-5 relative before:content-[''] before:absolute before:left-1.5 before:top-2 before:h-1.5 before:w-1.5 before:bg-primary-500 before:rounded-full"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Today's Alerts */}
              {briefing.todayAlerts.length > 0 && (
                <div className="space-y-3 pt-3 border-t border-border/60">
                  <h3 className="text-xs font-bold text-text-primary uppercase tracking-wider flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4 text-warning" />
                    Richiede Attenzione Oggi
                  </h3>
                  <ul className="space-y-2">
                    {briefing.todayAlerts.map((item, idx) => (
                      <li
                        key={idx}
                        className="text-xs text-warning-strong leading-relaxed bg-warning-soft/20 border border-warning/10 rounded-lg p-2.5 flex items-start gap-2"
                      >
                        <Info className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* AI Strategic Recommendation */}
              <div className="space-y-2 pt-4 border-t border-border/60">
                <span className="text-xs font-bold text-primary-600 uppercase tracking-wider block">
                  Raccomandazione Strategica AI
                </span>
                <div className="bg-primary-500/5 border border-primary-500/20 p-4 rounded-xl text-xs text-text-secondary leading-relaxed">
                  {briefing.aiRecommendation}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="text-center p-8 bg-card rounded-xl border border-border">
            Nessun briefing disponibile. Clicca per generare il primo.
          </div>
        )}

        {/* Focus Regeneration Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-bold">
              Personalizza Briefing AI
            </CardTitle>
            <CardDescription>
              Fornisci istruzioni o focus tematici all'AI per rigenerare un
              briefing su misura.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegenerate} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="custom-prompt">
                  Focus del Report (Facoltativo)
                </Label>
                <Input
                  id="custom-prompt"
                  placeholder="es. focus su costi e marketing budget, oppure rischi operativi..."
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="flex items-center justify-between gap-4 pt-2">
                <span className="text-xs text-text-muted">
                  {message && (
                    <span className="text-primary-600 font-semibold">
                      {message}
                    </span>
                  )}
                </span>
                <Button
                  type="submit"
                  loading={loading}
                  className="gap-1.5 shrink-0"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                  />
                  Rigenera con AI
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Semantic Ledger Logs (Right Sidebar) */}
      <div className="space-y-6">
        <Card className="h-[620px] flex flex-col justify-between">
          <CardHeader className="pb-3 border-b border-border/60">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-text-muted" />
              <CardTitle className="text-sm font-bold text-text-primary">
                Semantic Ledger Timeline
              </CardTitle>
            </div>
            <CardDescription className="text-[11px]">
              Attività recenti del workspace utilizzate come sorgente dati.
            </CardDescription>
          </CardHeader>

          <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
            {logs.map((log) => (
              <div
                key={log.id}
                className="text-xs space-y-1.5 border border-border/60 rounded-xl p-3 bg-card-muted/10"
              >
                <div className="flex justify-between items-center flex-wrap gap-1">
                  <Badge
                    variant={getNodeBadgeVariant(log.nodeType) as any}
                    className="text-[8px] uppercase px-1.5 py-0"
                  >
                    {log.nodeType}
                  </Badge>
                  <span className="text-[9px] text-text-muted">
                    {new Date(log.createdAt).toLocaleTimeString("it-IT", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <p className="text-[11px] text-text-primary leading-relaxed font-medium">
                  {log.description}
                </p>
                <div className="text-[9px] text-text-muted pt-1 border-t border-border/20 flex justify-between">
                  <span>Autore: {log.authorName || "Sistema"}</span>
                  <span className="uppercase font-bold text-[8px]">
                    {log.eventType}
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
